import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import PhilosophySection from './components/PhilosophySection';
import TeamSection from './components/TeamSection';
import OfficeSection from './components/OfficeSection';
import TimelineSection from './components/TimelineSection';
import ValuesSection from './components/ValuesSection';

const AboutPage = () => {
  useEffect(() => {
    // Scroll reveal animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.scroll-reveal');
    revealElements?.forEach(el => observer?.observe(el));

    return () => {
      revealElements?.forEach(el => observer?.unobserve(el));
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>About REXFEL - Strategic Investment Meets Digital Innovation</title>
        <meta 
          name="description" 
          content="Learn about REXFEL's investment philosophy, Hamburg-based team expertise, and strategic methodology. Discover how we architect digital futures through calculated boldness and operational excellence." 
        />
        <meta name="keywords" content="REXFEL, about, investment philosophy, Hamburg office, team expertise, strategic investment, venture capital, digital innovation" />
        <meta property="og:title" content="About REXFEL - Strategic Investment Meets Digital Innovation" />
        <meta property="og:description" content="Hamburg-based strategic investment firm representing calculated boldness. Meet our team and discover our unique venture-building methodology." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/about" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <HeroSection />

        {/* Investment Philosophy */}
        <PhilosophySection />

        {/* Team Expertise */}
        <TeamSection />

        {/* Hamburg Office */}
        <OfficeSection />

        {/* Company Timeline */}
        <TimelineSection />

        {/* Values & Culture */}
        <ValuesSection />
      </div>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-brand-sm bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <span className="text-white font-bold text-lg font-mono">R</span>
                </div>
                <div>
                  <h3 className="font-bold text-foreground">REXFEL</h3>
                  <p className="text-xs text-muted-foreground">Technology and Innovation</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Strategic investment meets digital innovation. Empowering European startups to scale globally.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/portfolio" className="hover:text-primary transition-brand">Portfolio</a></li>
                <li><a href="/crypto-dashboard" className="hover:text-primary transition-brand">Crypto Dashboard</a></li>
                <li><a href="/news" className="hover:text-primary transition-brand">Market Insights</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/about" className="hover:text-primary transition-brand">About Us</a></li>
                <li><a href="/contact" className="hover:text-primary transition-brand">Contact</a></li>
                <li><a href="/legal-hub" className="hover:text-primary transition-brand">Legal Hub</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Hamburg Office</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Wandsbek District</p>
                <p>22119 Hamburg, Germany</p>
                <p>+49 172 7999621</p>
                <p>info@rexfel.com</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <span className="text-xs text-muted-foreground">Hamburg Chamber of Commerce</span>
              <span className="text-xs text-muted-foreground">EU Regulated</span>
              <span className="text-xs text-muted-foreground">GDPR Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default AboutPage;